/**
 * 100ViewsOnly — Push Notification Service
 * Handles browser push permission and drop notifications
 */

import { createUserNotification } from "./notificationsInboxService";
import {
  addDoc, collection, getDocs, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Request push notification permission from the browser
 * Returns "granted", "denied", or "default"
 */
export async function requestPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";

  const result = await Notification.requestPermission();
  return result;
}

/**
 * Check current permission status without prompting
 */
export function getPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/**
 * Show a local browser notification (no server needed)
 * Used for immediate drop alerts
 */
export function showLocalNotification({ title, body, icon, url }) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;

  const n = new Notification(title, {
    body,
    icon: icon || "/logo192.png",
    badge: "/logo192.png",
    tag: url || "100viewsonly",
    data: { url }
  });

  n.onclick = () => {
    window.focus();
    if (url) window.location.href = url;
    n.close();
  };
}

/**
 * Notify all followers of a creator when a drop goes live
 * Creates notification documents in Firestore for each follower
 */
export async function notifyFollowersOfDrop({
  creatorId,
  creatorDisplayName,
  creatorUsername,
  creatorAvatar,
  dropId,
  dropTitle,
  viewLimit = 100
}) {
  if (!creatorId || !dropId) return;

  try {
    const followSnap = await getDocs(query(
      collection(db, "follows"),
      where("creatorId", "==", creatorId)
    ));

    if (followSnap.empty) return;

    const followerIds = followSnap.docs.map(d => d.data().followerId).filter(Boolean);

    const batches = [];
    for (let i = 0; i < followerIds.length; i += 30) {
      batches.push(followerIds.slice(i, i + 30));
    }

    for (const batch of batches) {
      await Promise.all(batch.map((userId) =>
        createUserNotification(userId, {
          type: "drop",
          creatorId,
          creatorDisplayName,
          creatorUsername,
          creatorAvatar: creatorAvatar || "",
          title: creatorDisplayName,
          text: `just dropped something — "${dropTitle}" · ${viewLimit} free views available`,
          dropId,
          link: `/drop/${dropId}`,
        })
      ));
    }

    console.log(`✅ Notified ${followerIds.length} followers of drop`);
  } catch (err) {
    console.error("Notify followers error:", err);
  }
}

/**
 * Save push subscription to Firestore for a user
 * (For future server-side push with VAPID keys)
 */
export async function savePushSubscription(userId, subscription) {
  if (!userId || !subscription) return;
  try {
    await addDoc(collection(db, "pushSubscriptions"), {
      userId,
      subscription: JSON.stringify(subscription),
      createdAt: serverTimestamp()
    });
  } catch (err) {
    console.error("Save push subscription error:", err);
  }
}